import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { PasswordValidator } from '../../interfaces/user-interfaces';

@Injectable({
  providedIn: 'root'
})
export class AuthValidatorService {
  // First Name
  firstNameRequired = '';
  firstNameMinLength = '';
  firstNameMaxLength = '';

  // Last Name
  lastNameRequired = '';
  lastNameMinLength = '';
  lastNameMaxLength = '';

  // Password
  passwordRequired = '';
  passwordMinLength = '';
  passwordHasUpperCase = '';
  passwordHasLowerCase = '';
  passwordHasNumber = '';
  passwordHasSpecialChar = '';

  // Temp Password
  tempPasswordInvalid = '';

  constructor(private translocoService: TranslocoService) {
    // Populate with translations
    combineLatest([
      this.translocoService.selectTranslate('shared.validators.firstName.required'),
      this.translocoService.selectTranslate('shared.validators.firstName.minLength'),
      this.translocoService.selectTranslate('shared.validators.firstName.maxLength'),
      this.translocoService.selectTranslate('shared.validators.lastName.required'),
      this.translocoService.selectTranslate('shared.validators.lastName.minLength'),
      this.translocoService.selectTranslate('shared.validators.lastName.maxLength'),
      this.translocoService.selectTranslate('shared.validators.password.required'),
      this.translocoService.selectTranslate('shared.validators.password.minLength'),
      this.translocoService.selectTranslate('shared.validators.password.hasUpperCase'),
      this.translocoService.selectTranslate('shared.validators.password.hasLowerCase'),
      this.translocoService.selectTranslate('shared.validators.password.hasNumber'),
      this.translocoService.selectTranslate('shared.validators.password.hasSpecialChar'),
      this.translocoService.selectTranslate('auth.invalid_password'),
    ]).subscribe(
      ([
        firstNameRequired, firstNameMinLength, firstNameMaxLength,
        lastNameRequired, lastNameMinLength, lastNameMaxLength,
        passwordRequired, passwordMinLength, passwordHasUpperCase,
        passwordHasLowerCase, passwordHasNumber, passwordHasSpecialChar,
        invalidPasswordMessage
      ]) => {
        this.firstNameRequired = firstNameRequired;
        this.firstNameMinLength = firstNameMinLength;
        this.firstNameMaxLength = firstNameMaxLength;
        this.lastNameRequired = lastNameRequired;
        this.lastNameMinLength = lastNameMinLength;
        this.lastNameMaxLength = lastNameMaxLength;
        this.passwordRequired = passwordRequired;
        this.passwordMinLength = passwordMinLength;
        this.passwordHasUpperCase = passwordHasUpperCase;
        this.passwordHasLowerCase = passwordHasLowerCase;
        this.passwordHasNumber = passwordHasNumber;
        this.passwordHasSpecialChar = passwordHasSpecialChar;
        this.tempPasswordInvalid = invalidPasswordMessage;
      }
    );
  }

  validateFirstName(firstName: string): string | null {
    if (!firstName || firstName.trim().length === 0) {
      return this.firstNameRequired;
    }
    if (firstName.length < 2) {
      return this.firstNameMinLength;
    }
    if (firstName.length > 50) {
      return this.firstNameMaxLength;
    }
    const specialCharRegex = /[^a-zA-ZÀ-ÖØ-öø-ÿ\s'-]/;
    if (specialCharRegex.test(firstName)) {
      return this.translocoService.translate('shared.validators.firstName.invalid');
    }
    return null;
  }

  validateLastName(lastName: string): string | null {
    if (!lastName || lastName.trim().length === 0) {
      return this.lastNameRequired;
    }
    if (lastName.length < 2) {
      return this.lastNameMinLength;
    }
    if (lastName.length > 50) {
      return this.lastNameMaxLength;
    }
    const specialCharRegex = /[^a-zA-ZÀ-ÖØ-öø-ÿ\s'-]/;
    if (specialCharRegex.test(lastName)) {
      return this.translocoService.translate('shared.validators.lastName.invalid');;
    }
    return null;
  }

  /*
    - The password must not be empty
    - The password must have a minimum length of 8 characters
    - The password must include at least:
      - One uppercase letter (A-Z)
      - One lowercase letter (a-z)
      - One numeric digit (0-9)
      - One special character (e.g., !@#$%^&*(),.?":{}|<>)
  */
  validatePassword(password: string): string | null {
    if (!password || password.trim().length === 0) {
      return this.passwordRequired;
    }
    if (password.length < 8) {
      return this.passwordMinLength;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase) {
      return this.passwordHasUpperCase;
    }
    if (!hasLowerCase) {
      return this.passwordHasLowerCase;
    }
    if (!hasNumber) {
      return this.passwordHasNumber;
    }
    if (!hasSpecialChar) {
      return this.passwordHasSpecialChar;
    }
    return null;
  }

  validatePasswordVisualy(password: string): PasswordValidator {
    var passwordValidator: PasswordValidator = {
      length: true,
      uppercase: true,
      lowercase: true,
      number: true,
      special: true
    };
    if (!password || password.trim().length === 0) {
      passwordValidator.length = false;
    }
    if (password.length < 8) {
      passwordValidator.length = false;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase) {
      passwordValidator.uppercase = false;
    }
    if (!hasLowerCase) {
      passwordValidator.lowercase = false;
    }
    if (!hasNumber) {
      passwordValidator.number = false;
    }
    if (!hasSpecialChar) {
      passwordValidator.special = false;
    }
    return passwordValidator;
  }

  validateTempPassword(password: string): string | null {
    if (!password || password.trim().length === 0) {
      return this.passwordRequired;
    }

    if (password.length !== 6) {
      return this.tempPasswordInvalid;
    }
    return null;
  }

  validateEmail(email: string): string | null {
    if (!email || email.trim().length === 0) {
      return this.translocoService.translate('shared.validators.email.required');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return this.translocoService.translate('shared.validators.email.invalid');
    }
    return null;
  }

  validateCompanyName(companyName: string): string | null {
    if (!companyName || companyName.trim().length === 0) {
      return this.translocoService.translate('shared.validators.companyName.required');
    }

    if (companyName.length < 2) {
      return this.translocoService.translate('shared.validators.companyName.minLength');
    }

    if (companyName.length > 100) {
      return this.translocoService.translate('shared.validators.companyName.maxLength');
    }

    const specialCharRegex = /[^a-zA-Z0-9\s&\-,.']/;
    if (specialCharRegex.test(companyName)) {
      return this.translocoService.translate('shared.validators.companyName.invalid');
    }
    return null;
  }

  validateIndustryCode(industryCode: string): string | null {
    if (!industryCode || industryCode.trim().length === 0) {
      return this.translocoService.translate('shared.validators.industryCode.required');
    }

    if (industryCode.length > 50) {
      return this.translocoService.translate('shared.validators.industryCode.maxLength');
    }
    return null;
  }

  validateAboutUs(aboutUs: string): string | null {
    if (!aboutUs || aboutUs.trim().length === 0) {
      return this.translocoService.translate('shared.validators.aboutUs.required');
    }
    if (aboutUs.length < 2) {
      return this.translocoService.translate('shared.validators.aboutUs.minLength');
    }
    if (aboutUs.length > 1000) {
      return this.translocoService.translate('shared.validators.aboutUs.maxLength');
    }
    return null;
  }
}
